import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Stethoscope, FlaskConical, ScanLine, Pill, ClipboardList, FileCheck2, Video, HeartPulse, History,
} from "lucide-react";

const META = {
  consulta: { icon: Stethoscope, color: "text-primary", bg: "bg-primary/10", label: "Consulta" },
  exame_lab: { icon: FlaskConical, color: "text-ai", bg: "bg-ai/10", label: "Exame laboratorial" },
  imagem: { icon: ScanLine, color: "text-chart-3", bg: "bg-chart-3/10", label: "Imagem" },
  receita: { icon: Pill, color: "text-accent-ai", bg: "bg-ai/10", label: "Receita" },
  pedido: { icon: ClipboardList, color: "text-warning", bg: "bg-warning/10", label: "Pedido de exame" },
  atestado: { icon: FileCheck2, color: "text-success", bg: "bg-success/10", label: "Atestado" },
  teleconsulta: { icon: Video, color: "text-primary", bg: "bg-primary/10", label: "Teleconsulta" },
  wearable: { icon: HeartPulse, color: "text-destructive", bg: "bg-destructive/10", label: "Sinais vitais" },
};

function fmtDate(d) {
  try { return new Date(d + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function TimelineTab({ patientId }) {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("todos");

  useEffect(() => { api.get(`/patients/${patientId}/timeline`).then((r) => setEvents(r.data || [])); }, [patientId]);

  const types = ["todos", ...Array.from(new Set(events.map((e) => e.type)))];
  const filtered = filter === "todos" ? events : events.filter((e) => e.type === filter);

  return (
    <div className="space-y-6" data-testid="timeline-tab">
      <div>
        <h3 className="font-heading text-xl font-medium flex items-center gap-2"><History className="w-5 h-5 text-ai" /> Linha do tempo do paciente</h3>
        <p className="text-sm text-muted-foreground">Todos os eventos clínicos em ordem cronológica — evolução completa num só lugar.</p>
      </div>

      {events.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed" data-testid="timeline-empty">
          Ainda não há eventos registados para este paciente.
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button key={t} onClick={() => setFilter(t)} data-testid={`timeline-filter-${t}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === t ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {t === "todos" ? "Todos" : (META[t]?.label || t)}
              </button>
            ))}
          </div>

          <div className="relative pl-6" data-testid="timeline-list">
            <div className="absolute left-[9px] top-1 bottom-1 w-px bg-border" />
            <div className="space-y-4">
              {filtered.map((e, i) => {
                const m = META[e.type] || META.consulta;
                const Icon = m.icon;
                return (
                  <div key={i} className="relative" data-testid={`timeline-item-${i}`}>
                    <div className={`absolute -left-6 top-1 w-[19px] h-[19px] rounded-full ${m.bg} flex items-center justify-center ring-4 ring-background`}>
                      <Icon className={`w-3 h-3 ${m.color}`} />
                    </div>
                    <Card className="p-4 border-border shadow-none ml-2">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="text-xs font-medium text-muted-foreground">{fmtDate(e.date)}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${m.bg} ${m.color}`}>{m.label}</span>
                      </div>
                      <p className="font-medium mt-1">{e.title}</p>
                      {e.subtitle && <p className="text-sm text-muted-foreground">{e.subtitle}</p>}
                      {e.by && <p className="text-xs text-muted-foreground/70 mt-1">{e.by}</p>}
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
